import { Scale } from 'lucide-react'
import type { CategoryState, ExtractedCriterion } from '@/types/extraction'
import { OverviewStatTile } from '@/components/overview/OverviewStatTile'
import { useCoverageStore } from '@/stores/coverage-store'
import { isRanAndEmpty } from '@/stores/extraction-store'

export function EvalCriteriaSummary({
  bidId,
  criteria,
  categoryState,
  loading,
}: {
  bidId: string
  criteria: ExtractedCriterion[]
  categoryState: CategoryState
  loading?: boolean
}) {
  const coverage = useCoverageStore((s) => s.coverage)
  const coverageBidId = useCoverageStore((s) => s.bidId)

  const total = criteria.length
  const totalWeight = criteria.reduce((sum, item) => sum + (item.weightPercentage ?? 0), 0)
  // ComplianceSnapshot (same overview grid) triggers the coverage load.
  const summary = coverageBidId === bidId ? coverage?.criteriaSummary : undefined
  // Use the shared selector rather than re-deriving "ran and found nothing"
  // inline, so this copy can never disagree with the tab-collapse decision in
  // AnalysisSection or the empty state in ExtractionTable.
  const ranAndEmpty = isRanAndEmpty(categoryState)

  return (
    <OverviewStatTile
      icon={Scale}
      label="Evaluation criteria"
      value={total}
      // `loading` only gates the tile's value, so the sub line has to be held
      // back here too — mid-run the counts are still zero and the tile would
      // otherwise claim a verdict while the analysis is still finding out.
      sub={
        loading ? undefined : total > 0 ? (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="shrink-0">{totalWeight > 0 ? `${totalWeight}% weighted` : 'Unweighted'}</span>
            {/* Carry the denominator: the headline counts TOP-LEVEL criteria
                while coverage is measured on LEAF criteria (`criteriaSummary`
                summarises `criteria.filter(c => !c.isRollup)`, so a parent with
                children is excluded and its children counted instead). A bare
                "13 unmapped" beside a headline of 7 reads as 13 of 7. On the
                bid this was found on: 7 top-level, 17 total, 10 children. The
                sibling ComplianceSnapshot has no such split — its headline is
                drawn from the same summary. */}
            {summary && summary.unmapped > 0 && (
              <span className="shrink-0 font-medium text-[hsl(var(--status-warning))]">
                {summary.unmapped} of {summary.total} unmapped
              </span>
            )}
          </span>
        ) : ranAndEmpty ? (
          'Nothing found'
        ) : (
          'Not analysed yet'
        )
      }
      to={`/app/bids/${bidId}/qualify?view=evaluation_criteria`}
      loading={loading}
    />
  )
}
